import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconsSend from '@mui/icons-material/Send';
import GraphQLTable from '../../../../../components/_table/GraphQLTable';
import {
  IProductQueryData,
  IProductVars,
  useProductQuery,
} from '../../../../../../graphql/query/product/useProductQuery';
import { IProductSchema } from '../../../../../../graphql/type/product';
import DropshippedRequestForDropshipDetailTableItem from './DropshippedRequestForDropshipDetailTableItem';
import useNotification from '../../../../../components/_hook/useNotification';
import useRouter from '../../../../../components/_hook/useRouter';
import { useCreateDropshippingProductRequestsMutation } from '../../../../../../graphql/mutation/dropshippingRequestProduct/createDropshippingProductRequestsMutation';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import { IProductQueryDropshippingProductRequestsParameter } from '../../../../../../graphql/query/product/subqueryParameter/productQueryDropshippingProductRequestParameter';

export default function DropshipedRequestForDropshipDetailTable() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { params } = useRouter();
  const { companyID, toCompanyID } = params;
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IProductSchema[]>([]);

  const [
    createDropshippingProductRequestsMutation,
  ] = useCreateDropshippingProductRequestsMutation(
    `createdDropshippingProductRequests {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(t('done'));
        setSelectedItems([]);
        setTableRefreshKey(new Date().getTime());
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickCreateDropshippingProductRequests() {
    await createDropshippingProductRequestsMutation({
      variables: {
        companyId: companyID,
        productIds: selectedItems.map((item) => item._id),
      },
    });
  }

  const dropshippingRequestProductFromCompanyId: IProductQueryDropshippingProductRequestsParameter['dropshippingRequestProductFromCompanyId'] = companyID;

  return (
    <>
      <GraphQLTable<IProductQueryData, IProductSchema, IProductVars>
        refreshKey={tableRefreshKey}
        select={`
				_id
				ordering
				company
				companyData {
				    _id
					origin
					slug
					aka {
                        la
                        text
                    }
					name {
                        la
                        text
                    }
				}
				name {
					la
					text
				}
				slug
				status 
				featured 
				prices {
					final
					currency
				}
				media {
					images {
						status 
						filename
						filename_small
		
						filenameUrl
						filenameSmallUrl
					}
				}
				dropshipping {
				  enable
				}
				dropshippingProductRequests(dropshippingRequestProductFromCompanyId: "${dropshippingRequestProductFromCompanyId}") {
				  _id 
				  status
				}
				updated {
					time 
				}
				created {
					time 
				}
       	 `}
        useQuery={useProductQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'name',
            label: t('name'),
            align: 'left',
          },
          {
            field: 'image',
            label: t('image'),
            align: 'center',
          },
          {
            field: 'finalPrice',
            label: t('price'),
            align: 'center',
            sortable: true,
          },
          {
            field: 'request_status',
            label: t('request_status'),
            align: 'center',
          },
        ]}
        renderItem={(item) => (
          <DropshippedRequestForDropshipDetailTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        defaultFilterOptions={{
          companyId: toCompanyID,
          isDropshippingEnable: true,
        }}
        searchFields={{
          _id: { label: t('id') },
          name: { label: t('name') },
        }}
        title={t('dropshipping_product')}
        defaultSort="ordering"
        defaultSortOrder={1}
        itemKey="_id"
        actionButtons={[
          <ButtonSubmit
            key="createDropshippingProductRequests"
            variant="contained"
            color="primary"
            size="small"
            label={t('send_request')}
            icon={<IconsSend />}
            onClick={onClickCreateDropshippingProductRequests}
          />,
        ]}
      />
    </>
  );
}
